import { useMemo } from "react";
import useSWR from "swr";
import type { HeldCredit } from "@studiolite/booking";
import { swrFetcher } from "@/lib/api";
import { useAuthStore } from "@/store/auth-store";

type WidgetPass = {
  id: string;
  status: string;
  /** Expiration effective, fermetures du studio comprises. */
  expiryDate: string | null;
  classesLeft: number | null;
  /** Type de carte : un atelier n'accepte que certaines cartes. */
  passId?: string | null;
  passType?: { name?: string | null } | null;
};

type WidgetSubscription = {
  id: string;
  title: string | null;
  status: string;
  currentPeriodEnd: string | null;
  creditsRemaining: number | null;
};

/**
 * Les passes et abonnements de l'eleve, sous la forme des credits du
 * paiement (HeldCredit), comme useStudentCredits sur le site du studio : la
 * reservation en un geste et le paiement choisissent le meme credit.
 */
export function useStudentCredits() {
  const user = useAuthStore((s) => s.user);

  const { data: passes, isLoading: passesLoading, mutate: refreshPasses } = useSWR<WidgetPass[]>(
    user ? "/api/widget/profile/passes" : null,
    swrFetcher,
  );
  const { data: held, isLoading: subsLoading, mutate: refreshSubs } = useSWR<{
    subscriptions: WidgetSubscription[];
  }>(user ? `/api/widget/profile/${user.id}/passes-subscriptions` : null, swrFetcher);
  const { data: oneTime } = useSWR<{ passIds: string[] }>(
    user ? "/api/widget/profile/passes/one-time-purchased" : null,
    swrFetcher,
  );

  const credits = useMemo<HeldCredit[]>(
    () => [
      ...(passes ?? []).map((p) => ({
        id: p.id,
        kind: "pass" as const,
        title: p.passType?.name ?? "",
        status: p.status,
        creditsRemaining: p.classesLeft,
        expiresAt: p.expiryDate,
        passId: p.passId ?? null,
      })),
      ...(held?.subscriptions ?? []).map((s) => ({
        id: s.id,
        kind: "membership" as const,
        title: s.title ?? "",
        status: s.status,
        creditsRemaining: s.creditsRemaining,
        expiresAt: s.currentPeriodEnd,
      })),
    ],
    [passes, held],
  );

  return {
    credits,
    /** Cartes a usage unique deja achetees : l'essai n'est pas propose deux fois. */
    boughtPassIds: oneTime?.passIds ?? [],
    isLoading: !!user && (passesLoading || subsLoading),
    refresh: () => Promise.all([refreshPasses(), refreshSubs()]),
  };
}
